/* eslint-disable max-len */
import React, { useState, useEffect } from 'react';
import { connect, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { I18n } from 'react-redux-i18n';
import { Helmet } from 'react-helmet';
import { AnimatePresence, motion } from 'framer-motion';
import {
  Grid, View, Heading, Text,
  Divider,
  Button,
  Flex,
} from '@adobe/react-spectrum';
import { getPartnerDetail } from '../../actions/partnerDetailAction';
import { getAllFacetData } from '../../actions/facetsActions';

import { withRouter } from '../../components/withRouter';
import { LoadingAnimation, PageVariants, PageTransitions } from '../../util/FramerMotionConfig';

import HeroBanner from '../../components/Details/HeroBanner';
import ReturnToResultsButton from '../../components/Details/ReturnToResultsButton';
import OpenURLButton from '../../components/Details/OpenURLButton';
import ViewPhoneNumberButton from '../../components/Details/ViewPhoneNumberButton';
import PartnerInfoCard from '../../components/Details/PartnerInfoCard';

import '../../styles/DetailPage.scss';
import ContactUs from '../List/Action/ContactUs';
import SocialMediaIcons from '../../components/Filters/SocialMediaIcons';
import { SET_COMPANY_DETAILS, SET_IS_OPEN, SET_PROGRAM_TYPE } from '../../constants';
import Accordion from '../../components/global/Accordion/Accordion';
import { detailPageLoadingSpinner, mobileInfo, renderAvailableTabs } from './utils';

const DetailPage = ({
  router,
  partnerDetail,
  getPartnerDetailAction,
  getAllFacetDataAction,
  solution,
  isOpen,
  companyDetails,
  programPath,
  isMobile,
}) => {
  const [defaultTab, setDefaultTab] = useState('companyInfo');
  const [visibleTabs, setVisibleTabs] = useState([]);
  const [initiatives, setInitiatives] = useState([]);
  const dispatch = useDispatch();
  const expertiseTabIndex = 0;
  const { programType } = router.params;

  useEffect(() => {
    dispatch({ type: SET_PROGRAM_TYPE, payload: { programPath: programType === 'technology' ? 'mxp' : 'spp' } });
  }, [programType]);

  useEffect(() => {
    if (solution?.options?.length === 0) {
      getAllFacetDataAction();
    }
  }, [getAllFacetDataAction, solution]);

  useEffect(() => {
    if (!_.has(partnerDetail, 'companyInfo.listingUrl') || decodeURI(partnerDetail.companyInfo.listingUrl) !== router?.params?.id) {
      getPartnerDetailAction(router?.params?.id);
    }

    // Set default tab based on URL hash
    const tabHashes = ['companyInfo', 'expertise', 'locations', 'resources'];
    const { hash } = window.location;

    if (hash && tabHashes.includes(hash.substring(1))) {
      setDefaultTab(hash.substring(1));
    }
  }, [router, partnerDetail, getPartnerDetailAction]);

  useEffect(() => {
    const { hash } = window.location;
    const tabHashes = ['companyInfo', 'expertise', 'locations', 'resources'];

    if (hash && tabHashes.includes(hash.split('#')[1])) {
      setDefaultTab(hash.split('#')[1]);
    }
  }, []);

  useEffect(() => {
    setInitiatives(partnerDetail?.resources?.Initiative?.filter((item) => item.active) || []);
  }, [partnerDetail?.resources?.Initiative]);

  const getPartnerData = (attr) => _.get(partnerDetail, attr, '');

  const handleContactUsModal = (id, name) => {
    dispatch({ type: SET_IS_OPEN, payload: !isOpen });
    dispatch({
      type: SET_COMPANY_DETAILS,
      payload: {
        id,
        name,
      },
    });
  };

  const handleContactUsClear = () => {
    dispatch({ type: SET_IS_OPEN, payload: false });
    dispatch({
      type: SET_COMPANY_DETAILS,
      payload: {},
    });
  };

  const handleTabChange = (selectedTab) => {
    setDefaultTab(selectedTab);
    if (window.history.replaceState) {
      window.history.replaceState(null, null, `#${selectedTab}`);
    } else {
      window.location.hash = `#${selectedTab}`;
    }
  };

  useEffect(() => {
    const newVisibleTabs = [];

    const {
      companyInfo, expertise, deployments, accreditedSolutions, industries, services, addresses, specializations,
    } = partnerDetail;

    if (companyInfo?.shortDescription || industries?.length > 0 || services?.length > 0) {
      newVisibleTabs.push('companyInfo');
    }

    if (expertise?.length > 0 || _.size(deployments) || accreditedSolutions?.length > 0 || specializations?.length > 0) {
      newVisibleTabs.push('expertise');
    }

    if (addresses?.length > 0) {
      newVisibleTabs.push('locations');
    }

    if (
      _.has(partnerDetail, 'resources')
      && _.size(partnerDetail.resources) > 0
      && _.some(['Video', 'Success story', 'White paper', 'Data sheet'], (key) => _.has(partnerDetail.resources, key))
    ) {
      newVisibleTabs.push('resources');
    }

    setVisibleTabs(newVisibleTabs);
  }, [partnerDetail, defaultTab]);

  return !partnerDetail.error ? (
    <motion.div exit="out" animate="in" initial="out" variants={PageVariants} transition={PageTransitions}>
      <Helmet titleTemplate={`%s | ${I18n.t('partner_finder.seo.app_title')}`}>
        <title>{getPartnerData('companyInfo.name')}</title>
        <meta name="description" content={getPartnerData('companyInfo.shortDescription')} />
        <meta property="og:title" content={`${getPartnerData('companyInfo.name')} | ${I18n.t('partner_finder.seo.app_title')}`} />
        <meta property="og:url" content={window.location.href} />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content={I18n.t('partner_finder.seo.app_title')} />
        <meta property="og:image" content={`${process.env.PUBLIC_URL}/img/thumb.png`} />
        <meta property="og:description" content={getPartnerData('companyInfo.shortDescription')} />
        <meta property="og:image:width" content="800" />
        <meta property="og:image:height" content="480" />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content={`${getPartnerData('companyInfo.name')} | ${I18n.t('partner_finder.seo.app_title')}`} />
        <meta name="twitter:description" content={getPartnerData('companyInfo.shortDescription')} />
        <meta name="twitter:image" content={`${process.env.PUBLIC_URL}/img/thumb.png`} />
        <meta name="twitter:image:alt" content="Adobe" />
        <meta name="twitter:site" content="@Adobe" />
        <meta name="twitter:creator" content="@Adobe" />
      </Helmet>

      {detailPageLoadingSpinner(partnerDetail, LoadingAnimation, router)}
      <HeroBanner />
      <View id="heading-section-wrapper">
        <View gridArea="heading" UNSAFE_className="heading-section">
          {!partnerDetail.preview && <ReturnToResultsButton programType={programType} isMobile={false} />}
          <div className="partner-name-section">
            <Heading level={2}>{getPartnerData('companyInfo.name')}</Heading>
            <Text>{getPartnerData('companyInfo.location')}</Text>
            <div className="button-section">
              {!partnerDetail.preview ? (
                <Button
                  UNSAFE_style={{
                    background: ' #000000E6 0% 0% no-repeat padding-box',
                    color: '#FFFFFF',
                    cursor: 'pointer',
                    border: '2px solid #000000E6',
                    borderRadius: '20px',
                  }}
                  onPress={() => handleContactUsModal(partnerDetail.companyInfo.listingId, partnerDetail.companyInfo.name)}
                >
                  <Text UNSAFE_style={{ fontWeight: 'bold' }}>Contact Us</Text>
                </Button>
              ) : <div />}
              <div className="button-group">
                <SocialMediaIcons apiResponse={partnerDetail.companyInfo} detailsPage />
                <OpenURLButton
                  variant="primary"
                  translatePath="partner_finder.detail.visit_website"
                  websiteUrl={getPartnerData('companyInfo.website')}
                  target="_blank"
                />
                <ViewPhoneNumberButton phoneNumber={getPartnerData('companyInfo.phone')} />
              </div>
            </div>

          </div>
        </View>
      </View>
      {!isMobile
        ? (
          <>
            <Divider size="L" UNSAFE_style={{ backgroundColor: `${partnerDetail.companyInfo.accentColor}`, height: '4px', marginBottom: '30px' }} />
            <Grid id="detail-page-container">
              <View gridArea="info" UNSAFE_className="info-section">
                <PartnerInfoCard />
              </View>
              <View gridArea="main" UNSAFE_className="main-section">
                <div className="partner-tabs-section">
                  <AnimatePresence>
                    {renderAvailableTabs(
                      visibleTabs,
                      isMobile,
                      partnerDetail,
                      programType,
                      handleTabChange,
                      defaultTab,
                    )}
                  </AnimatePresence>
                </div>
              </View>
            </Grid>
          </>
        )
        : (
          <>
            <Grid id="detail-page-container">
              <View gridArea="info" UNSAFE_className="info-section">
                <PartnerInfoCard />
                { mobileInfo(
                  partnerDetail,
                  handleContactUsModal,
                  companyDetails,
                  isOpen,
                  handleContactUsClear,
                  programPath,
                ) }
              </View>
            </Grid>
            <Divider size="L" UNSAFE_style={{ backgroundColor: `${partnerDetail.companyInfo.accentColor}`, height: '4px', marginBottom: '30px' }} />
            {!partnerDetail.preview && programType !== 'technology'
            && (
            <Flex direction="row" justifyContent="end" marginBottom={30}>
              <ReturnToResultsButton programType={programType} isMobile />
            </Flex>
            )}
            {partnerDetail?.resources?.Badge?.length > 0 && (
            <Accordion
              title="Partner Awards"
            >
              <div style={{ maxHeight: '160px', overflowY: 'auto' }}>
                {partnerDetail?.resources?.Badge
                  .sort((a, b) => (a.name < b.name ? 1 : -1))
                  .map((item) => (
                    <div style={{ fontSize: '16px', color: '#222222', marginBottom: '10px' }}>
                      {item.name}
                    </div>
                  ))}
              </div>
            </Accordion>
            )}
            {initiatives.length > 0 && (
            <Accordion title="Initiatives">
              <div style={{ maxHeight: '160px', overflowY: 'auto' }}>
                {initiatives
                  .map((item) => (
                    <div style={{ fontSize: '16px', color: '#222222', marginBottom: '10px' }}>
                      {`${item.year} ${item.name}`}
                    </div>
                  ))}
              </div>
            </Accordion>
            )}
            <Grid id="detail-page-container">
              <View gridArea="main" UNSAFE_className="main-section">
                <div className="partner-tabs-section">
                  <AnimatePresence>
                    {renderAvailableTabs(
                      visibleTabs,
                      isMobile,
                      partnerDetail,
                      programType,
                      handleTabChange,
                      defaultTab,
                    )}
                  </AnimatePresence>
                </div>
              </View>
            </Grid>
          </>
        )}
      <ContactUs
        actionType="contactUs"
        initialData={{}}
        companyDetails={companyDetails}
        isOpen={isOpen}
        handleContactUsClear={handleContactUsClear}
        programPath={programPath}
      />
    </motion.div>
  ) : (
    <motion.div exit="out" animate="in" initial="out" variants={PageVariants} transition={PageTransitions}>
      {detailPageLoadingSpinner(partnerDetail, LoadingAnimation, router)}
    </motion.div>
  );
};

DetailPage.propTypes = {
  router: PropTypes.shape({
    params: PropTypes.shape({
      id: PropTypes.string,
    }),
  }).isRequired,
  partnerDetail: PropTypes.shape({
    preview: PropTypes.bool.isRequired,
    companyInfo: PropTypes.shape({
      listingId: PropTypes.string,
      accentColor: PropTypes.string,
      shortDescription: PropTypes.string,
      longDescription: PropTypes.string,
      listingUrl: PropTypes.string,
      name: PropTypes.string,
      globalParentName: PropTypes.string,
      globalParentId: PropTypes.string,
      specializations: PropTypes.number,
      certifications: PropTypes.number,
    }),
    contactForm: PropTypes.shape({
      description: PropTypes.string,
    }),
    totalCustomerDeploymentCount: PropTypes.number,
    expertise: PropTypes.arrayOf(PropTypes.object),
    specializations: PropTypes.arrayOf(PropTypes.object),
    deployments: PropTypes.objectOf(PropTypes.array),
    accreditedSolutions: PropTypes.arrayOf(PropTypes.object),
    industries: PropTypes.arrayOf(PropTypes.string),
    services: PropTypes.arrayOf(PropTypes.object),
    addresses: PropTypes.arrayOf(PropTypes.object),
    resources: PropTypes.shape({
      'Success story': PropTypes.arrayOf(PropTypes.object),
      Video: PropTypes.arrayOf(PropTypes.object),
      'White paper': PropTypes.arrayOf(PropTypes.object),
      'Data sheet': PropTypes.arrayOf(PropTypes.object),
    }),
    error: PropTypes.number,
  }).isRequired,
  getPartnerDetailAction: PropTypes.func.isRequired,
  getAllFacetDataAction: PropTypes.func.isRequired,
  solution: PropTypes.shape({
    options: PropTypes.arrayOf(PropTypes.object),
  }),
};

DetailPage.defaultProps = {
  solution: {},
};

const mapStateToProps = (state) => ({
  partnerDetail: state.partnerDetail,
  solution: state.facets.solution,
  isOpen: state.contactUs.isOpen,
  companyDetails: state.contactUs.companyDetails,
  programPath: state.facets.programPath,
  isMobile: state.facets.isMobile,
});

const mapDispatchToProps = {
  getPartnerDetailAction: getPartnerDetail,
  getAllFacetDataAction: getAllFacetData,
};

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(DetailPage));
